import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeRemoteRegraTributaria } from "../../../usecases/regra-tributaria/remote-regra-tributaria-factory";
import {
  RegraTributariaRequest,
  RegraTributariaResponse,
} from "../../../../../domain/models/regra-tributaria";
import RegrasTributariasPage from "../../../../../presentation/pages/admin/regras-tributarias";
import toastUtils from "../../../../../utils/ToastUtils";

const MakeRegrasTributarias: React.FC = () => {
  const service = useMemo(() => makeRemoteRegraTributaria(), []);

  const [regras, setRegras] = useState<RegraTributariaResponse[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRegra, setEditingRegra] = useState<RegraTributariaResponse | null>(null);
  const [saving, setSaving] = useState(false);

  // Toggle
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // --- Carregar lista ---
  const loadRegras = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await service.listar();
      setRegras(data);
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao carregar regras tributárias." });
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    loadRegras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Modal ---
  const handleOpenModal = useCallback((regra?: RegraTributariaResponse): void => {
    setEditingRegra(regra || null);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback((): void => {
    setModalVisible(false);
    setEditingRegra(null);
  }, []);

  // --- Criar / Atualizar ---
  const handleSave = useCallback(async (data: RegraTributariaRequest): Promise<void> => {
    try {
      setSaving(true);
      if (editingRegra) {
        await service.atualizar(editingRegra.id, data);
        toastUtils.showMessage({ type: "success", message: "Regra atualizada com sucesso!" });
      } else {
        await service.criar(data);
        toastUtils.showMessage({ type: "success", message: "Regra criada com sucesso!" });
      }
      handleCloseModal();
      loadRegras();
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao salvar regra." });
    } finally {
      setSaving(false);
    }
  }, [service, editingRegra, handleCloseModal, loadRegras]);

  // --- Toggle ativo ---
  const handleToggleAtivo = useCallback(async (id: number): Promise<void> => {
    try {
      setTogglingId(id);
      await service.toggleAtivo(id);
      await loadRegras();
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao alterar status." });
    } finally {
      setTogglingId(null);
    }
  }, [service, loadRegras]);

  // --- Deletar ---
  const handleDelete = useCallback(async (id: number): Promise<void> => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm("Tem certeza que deseja excluir esta regra?")) return;

    try {
      await service.remover(id);
      toastUtils.showMessage({ type: "success", message: "Regra excluída com sucesso!" });
      loadRegras();
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao excluir regra." });
    }
  }, [service, loadRegras]);

  return (
    <RegrasTributariasPage
      regras={regras}
      loading={loading}
      modalVisible={modalVisible}
      editingRegra={editingRegra}
      saving={saving}
      onOpenModal={handleOpenModal}
      onCloseModal={handleCloseModal}
      onSave={handleSave}
      onToggleAtivo={handleToggleAtivo}
      onDelete={handleDelete}
      togglingId={togglingId}
    />
  );
};

export default MakeRegrasTributarias;
